// src/components/TransactionFormDialog.tsx
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, PlusIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  CommandDialog,
} from "@/components/ui/command";
import { useAuth } from "@/context/AuthProvider";
import { FilePreview } from "./file-preview";

const schema = z.object({
  type: z.enum(["income", "expense"]),
  amount: z.coerce.number().positive(),
  description: z.string().min(1),
  date: z.string(),
  to_account_id: z.string().optional().nullable(),
  category_id: z.string().optional().nullable(),
});

export function TransactionFormDialog({
  open,
  setOpen,
  onSubmit,
  editTx,
  accountsMap,
  accountId,
  setEditTx,
}: any) {
  const fileRef = useRef(null);
  const { user } = useAuth();
  const [url, setUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      type: "expense",
      amount: undefined,
      description: "",
      date: new Date().toISOString().substring(0, 10),
      to_account_id: null,
      category_id: null,
    },
  });

  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const currentType = form.watch("type");

  useEffect(() => {
    setUrl(null);
    if (editTx) {
      form.reset(editTx);
      // getImage(editTx.attachment_url);
    }
  }, [editTx]);
  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from("categories")
        .select("id, name, type, icon")
        .eq("type", currentType);
      setCategories(data || []);
    };
    if (currentType) {
      fetchCategories();
    }
  }, [currentType]);

  const limpiar = (val) => {
    setOpen(val);
    setEditTx(null);
    form.reset({
      type: "expense",
      amount: undefined,
      description: "",
      date: new Date().toISOString().substring(0, 10),
      to_account_id: null,
      category_id: null,
    });
  };

  async function handleSubmit(values: any) {
    setIsLoading(true);
    const file = fileRef.current?.files?.[0];
    let uploadedUrl = null;
    if (file) {
      let path = `${user?.id}/${Date.now()}_${file.name}`;
      const { data, error } = await supabase.storage
        .from("attachments")
        .upload(`${path}`, file);

      if (!error && data?.path) {
        const { data: publicUrlData } = supabase.storage
          .from("attachments")
          .getPublicUrl(data.path);

        uploadedUrl = path;
      }
    }

    onSubmit({ ...values, attachment_url: uploadedUrl });
    limpiar(false);
    setIsLoading(false);
  }

  const getImage = async (url: string) => {
    if (!url) return "";
    let uploadedUrl: any = null;

    const { data: signedData, error: signedError } = await supabase.storage
      .from("attachments")
      .createSignedUrl(url, 60 * 60 * 24 * 7);

    if (!signedError && signedData?.signedUrl) {
      uploadedUrl = signedData.signedUrl;
    }
    setUrl(uploadedUrl);
  };
  return (
    <>
      <Dialog open={open} onOpenChange={limpiar}>
        <DialogTrigger asChild>
          <Button onClick={() => limpiar(true)}>
            <PlusIcon className="w-4 h-4 mr-2" /> Añadir
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>
            {editTx ? "Editar Transacción" : "Nueva Transacción"}
          </DialogTitle>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4 "
            >
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monto</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <>
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl className="w-full">
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="income">Ingreso</SelectItem>
                            <SelectItem value="expense">Egreso</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch("type") === "expense" && (
                    <FormField
                      control={form.control}
                      name="to_account_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Transferir entre cuentas (opcional)
                          </FormLabel>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                            disabled={editTx ? true : false}
                          >
                            <FormControl className="w-full">
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona cuenta" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.entries(accountsMap)
                                .filter(([aid]) => aid !== accountId)
                                .map(([aid, name]) => (
                                  <SelectItem key={aid} value={aid}>
                                    {name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <>
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoría</FormLabel>
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCategoryDialogOpen(true)}
                            className="w-full justify-between"
                          >
                            {field.value
                              ? categories.find((cat) => cat.id === field.value)
                                  ?.name || "Selecciona una categoría"
                              : "Selecciona una categoría"}
                          </Button>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fecha</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              </div>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nota</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <FormLabel className="mb-2">Adjuntar comprobante</FormLabel>
                {/* <img
                  width={50}
                  src={url}
                  onClick={() => setPreviewUrl(url)}
                ></img> */}
                {editTx?.attachment_url ? (
                  <FilePreview url={editTx?.attachment_url}> </FilePreview>
                ) : (
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.pdf"
                    className="w-full file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando...
                  </div>
                ) : (
                  "Guardar"
                )}
              </Button>
            </form>
          </Form>
        </DialogContent>

        <CommandDialog
          open={categoryDialogOpen}
          onOpenChange={setCategoryDialogOpen}
        >
          <CommandInput placeholder="Buscar categoría..." />
          <CommandList>
            {categories.map((cat) => (
              <CommandItem
                key={cat.id}
                value={cat.name}
                onSelect={() => {
                  form.setValue("category_id", cat.id);
                  setCategoryDialogOpen(false);
                }}
              >
                {cat.icon} {cat.name}
              </CommandItem>
            ))}
          </CommandList>
        </CommandDialog>
      </Dialog>
      <Dialog
        open={!!previewUrl}
        onOpenChange={(o) => !o && setPreviewUrl(null)}
      >
        <DialogContent>
          <img
            src={previewUrl || ""}
            alt="Vista previa"
            className="max-w-full max-h-[80vh] rounded"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
